import PropTypes from 'prop-types';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import {
  COMPACT_ROW_ID_SX,
  COMPACT_ROW_META_SX,
  COMPACT_ROW_NAME_SX,
  COMPACT_ROW_SX,
  MUTED_ICON_OPACITY,
  MUTED_TEXT_COLOR,
  OUTLINED_ROW_DIVIDER,
  OutlinedRowList,
  OutlinedRowListSkeleton,
  RowCheckbox,
} from 'src/components/CompactRowList';
import Iconify from 'src/components/Iconify';

const LINK_BUTTON_SX = {
  textTransform: 'none',
  color: '#87C8E0',
  fontSize: '0.74rem',
  '&:hover': { backgroundColor: 'rgba(135, 200, 224, 0.08)' },
};

// The managed base policies a supplemental component is generated for, with the two ways to bring
// another under management beneath them. The caller owns which are selected. Disabled, the rows
// read as usual and only the checkboxes grey out and stop responding.
const ManagedBasePolicyField = ({
  basePolicies,
  loading,
  isSelected,
  onToggle,
  disabled = false,
  disabledReason,
  error,
  onAddBasePolicy,
  onUseOtherBasePolicy,
}) => (
  <Stack gap={0.5}>
    <Tooltip title={disabled ? disabledReason || '' : ''} placement="top" arrow>
      <Box>
        <OutlinedRowList label="Managed Base Policies">
          {loading ? (
            <OutlinedRowListSkeleton />
          ) : basePolicies.length === 0 ? (
            <Typography sx={{ ...COMPACT_ROW_META_SX, whiteSpace: 'normal', px: 1, py: 0.75 }}>
              No managed base policies were found for this environment. Add one or bring an already
              detected base policy under management.
            </Typography>
          ) : (
            <Stack divider={OUTLINED_ROW_DIVIDER} sx={{ maxHeight: 240, overflowY: 'auto' }}>
              {basePolicies.map(policy => {
                const checked = isSelected(policy.policyId);
                const toggle = () => onToggle(policy.policyId, checked);
                const mutedColor = checked ? null : { color: MUTED_TEXT_COLOR };

                return (
                  <Stack
                    key={policy.policyId}
                    direction="row"
                    gap={1.25}
                    alignItems="center"
                    onClick={disabled ? undefined : toggle}
                    sx={{
                      ...COMPACT_ROW_SX,
                      ...(disabled ? { cursor: 'default', '&:hover': null } : null),
                    }}>
                    <Box
                      display="inline-flex"
                      sx={{ flexShrink: 0, opacity: checked ? 1 : MUTED_ICON_OPACITY }}>
                      <Iconify icon="mdi:shield-outline" width={20} height={20} color="#9CA3AF" />
                    </Box>
                    <Typography sx={{ ...COMPACT_ROW_NAME_SX, ...mutedColor }}>
                      {policy.policyName}
                    </Typography>
                    <Typography sx={{ ...COMPACT_ROW_ID_SX, ...mutedColor }}>
                      {policy.policyId}
                    </Typography>
                    {policy.environmentDefault ? (
                      <Typography sx={{ ...COMPACT_ROW_META_SX, ...mutedColor }}>
                        Environment default
                      </Typography>
                    ) : null}
                    <RowCheckbox
                      checked={checked}
                      disabled={disabled}
                      onChange={toggle}
                      label={`Generate for ${policy.policyName}`}
                    />
                  </Stack>
                );
              })}
            </Stack>
          )}
        </OutlinedRowList>
      </Box>
    </Tooltip>
    {error ? (
      <Typography variant="caption" sx={{ color: 'error.main', pl: 1.5 }}>
        {error}
      </Typography>
    ) : null}
    <Stack direction="row" gap={0.5} flexWrap="wrap">
      <Button
        type="button"
        variant="text"
        size="small"
        disabled={disabled}
        onClick={onAddBasePolicy}
        startIcon={<Iconify icon="mdi:plus" width={16} height={16} />}
        sx={LINK_BUTTON_SX}>
        Add a base policy
      </Button>
      <Button
        type="button"
        variant="text"
        size="small"
        disabled={disabled}
        onClick={onUseOtherBasePolicy}
        startIcon={<Iconify icon="mdi:shield-search" width={16} height={16} />}
        sx={LINK_BUTTON_SX}>
        Use other base policy
      </Button>
    </Stack>
  </Stack>
);

ManagedBasePolicyField.propTypes = {
  basePolicies: PropTypes.arrayOf(
    PropTypes.shape({
      environmentDefault: PropTypes.bool,
      policyId: PropTypes.string.isRequired,
      policyName: PropTypes.string.isRequired,
    }),
  ).isRequired,
  loading: PropTypes.bool,
  isSelected: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  disabledReason: PropTypes.string,
  error: PropTypes.string,
  onAddBasePolicy: PropTypes.func.isRequired,
  onUseOtherBasePolicy: PropTypes.func.isRequired,
};

export default ManagedBasePolicyField;
